import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApplications, useRetailers } from '../api/hooks';
import { useSession } from '../auth/session';
/* D75 — the ONE due-rule formatter. The Terms column states the rule, it does not invent one. */
import { dueRuleSourceWords, dueRuleWords } from './payables-view';
import type { Application, RetailerRow, RetailerStatus } from '../api/types';
import { Alert, Chip, Empty, Money, PageHeader, Panel, Spinner, Table, Tiles, type Column, type Tone } from '../ui';

const TONE: Record<RetailerStatus, Tone> = {
  ACTIVE: 'done', ON_HOLD: 'stop', DORMANT: 'lapse', WIND_DOWN: 'run', CLOSED: 'them',
};

/**
 * A-03 — every trade account on this portal, at whatever stage it has reached.
 *
 * **One screen, because it is one list.** An application and a retailer are the same trade
 * customer before and after a decision, and they had a menu entry each: you looked in two places
 * to answer "who are my accounts", and an application that had been waiting a fortnight was
 * invisible from the list it was waiting to join.
 *
 * So applications are ROWS here, at the top, marked as what they are and linking to A-02 where
 * the decision is made. A-02 is still a screen — it is a work queue with account setup, the
 * inventory budget, payment terms and the subscription beside it, and folding that into a table
 * would make the table the wrong shape for both jobs — but it is reached from here rather than
 * from the navigation.
 *
 * **Who sees the applications.** `retailer.view` goes to Sales and Finance; `retailer.approve`
 * only to Operations. That split is deliberate — Sales can see who the accounts are and must not
 * be able to admit one — so without the permission the rows and the count are simply absent,
 * rather than shown and refused.
 */

/** One row of the list: a retailer, or an application that is not one yet. */
type Row =
  | { kind: 'retailer'; id: string; retailer: RetailerRow }
  | { kind: 'application'; id: string; application: Application };

export function Retailers() {
  const { t } = useTranslation();
  const { can } = useSession();
  const retailers = useRetailers();
  const mayApprove = can('retailer.approve');
  // The hook runs either way — a conditional hook is not allowed — and the result is ignored
  // without the permission. The server refuses it too, so this is presentation, not a guard.
  const applications = useApplications();

  if (retailers.isLoading) return <Spinner />;
  if (retailers.error) return <Alert tone="stop">{retailers.error.message}</Alert>;

  const accounts = retailers.data ?? [];
  const waiting = mayApprove
    ? (applications.data ?? []).filter((a) => a.status === 'PENDING' || a.status === 'INFO_REQUESTED')
    : [];

  /*
   * Waiting first, then the accounts — which the API already returns newest first. An
   * application is the only row on this screen that is asking something of the person reading
   * it, so it belongs where the eye lands.
   */
  const rows: Row[] = [
    ...waiting.map((a): Row => ({ kind: 'application', id: `a-${a.id}`, application: a })),
    ...accounts.map((r): Row => ({ kind: 'retailer', id: r.id, retailer: r })),
  ];

  const active = accounts.filter((r) => r.status === 'ACTIVE').length;

  const columns: Column<Row>[] = [
    {
      header: 'Retailer',
      render: (row) => (row.kind === 'retailer' ? (
        <>
          <Link to={`/retailers/${row.retailer.id}`}>{row.retailer.name}</Link>
          <div className="mk-hint">{row.retailer.city ?? '—'}, {row.retailer.countryCode}</div>
        </>
      ) : (
        <>
          {/* To the decision, not to a detail page: an application has no account record yet. */}
          <Link to="/applications">{row.application.businessName}</Link>
          <div className="mk-hint">{row.application.city ?? '—'}, {row.application.countryCode}</div>
        </>
      )),
    },
    {
      header: 'Email',
      render: (row) => <span className="mk-dim">
        {row.kind === 'retailer' ? row.retailer.email ?? '—' : row.application.contact.email}
      </span>,
    },
    {
      header: 'Status',
      render: (row) => (row.kind === 'retailer'
        ? <Chip tone={TONE[row.retailer.status]}>{row.retailer.status.replace('_', ' ')}</Chip>
        : (
          <Chip tone={row.application.status === 'INFO_REQUESTED' ? 'them' : 'us'}>
            {row.application.status === 'INFO_REQUESTED' ? 'Information requested' : 'Application'}
          </Chip>
        )),
    },
    {
      header: 'Tier',
      render: (row) => (row.kind === 'retailer'
        ? <span className="mk-dim">{row.retailer.tier ?? '—'}</span>
        // An application's useful second fact is how long it has been waiting, not a tier it
        // has not been given yet.
        : <span className="mk-dim">{row.application.ageDays} days waiting</span>),
    },
    /* Credit limit was retired at v1.6 — the inventory budget replaced it (D37). */
    {
      header: 'Payable', numeric: true,
      /*
       * FN-02 — derived on every read, never stored, and one figure PER CURRENCY. It used to be
       * a single number labelled with the trading currency, summed across every currency in the
       * ledger, which for a retailer holding GBP and INR rows belonged to neither (AN-08).
       * Nothing owed shows as '—': zero would mean a settled account.
       */
      render: (row) => {
        if (row.kind === 'application') return <span className="mk-dim">—</span>;
        const { payables } = row.retailer;
        return payables.length === 0
          ? <span className="mk-dim">—</span>
          : <>{payables.map((p) => (
            <div key={p.currency}><Money amount={p.amount} currency={p.currency} /></div>
          ))}</>;
      },
    },
    {
      header: 'Terms',
      /*
       * D75 — the rule IN FORCE, from `dueRuleWords()`'s shortest reading.
       *
       * This cell printed `Net ${paymentTermsDays}`, which was wrong twice over. The column was
       * NOT NULL DEFAULT 30, so every account read "Net 30" whatever it had been given; and the
       * figure decided nothing anyway, so an account on a MONTH_END portal was labelled with a
       * rule the portal does not operate. A dash was no better — an account with no term of its
       * own is not an account with no terms, it is an account on the portal's rule.
       *
       * The source note is what makes the column answerable: Admin scanning A-03 needs to see
       * which of these accounts were given a term and which are riding the portal's rule.
       */
      render: (row) => {
        if (row.kind !== 'retailer') return <span className="mk-dim">—</span>;
        const words = dueRuleWords(row.retailer.dueRule, 'term');
        return (
          <span className="mk-dim" title={t(dueRuleSourceWords(row.retailer.dueRule).key)}>
            {t(words.key, words.values)}
            {row.retailer.dueRule.source === 'retailer' ? null : <> ·&nbsp;portal</>}
          </span>
        );
      },
    },
    {
      header: 'Account manager',
      render: (row) => <span className="mk-dim">
        {row.kind === 'retailer' ? row.retailer.accountManager ?? '—' : '—'}
      </span>,
    },
  ];

  return (
    <>
      <PageHeader title="Retailers" />
      <Tiles
        items={[
          { k: 'Accounts', v: accounts.length },
          { k: 'Active', v: active },
          ...(mayApprove
            ? [{
              k: 'Waiting to join',
              v: waiting.length,
              // Only when there is something to do: a link to an empty queue is a dead end.
              s: waiting.length > 0 ? <Link to="/applications">Review</Link> : undefined,
            }]
            : []),
        ]}
      />
      <Panel>
        <Table<Row>
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          filterLabel="Filter by name, place, status or manager"
          /*
           * Everything a person would type: the name they half-remember, the city, the status
           * word they can see in the column, the account manager. Status is included because
           * "closed" is the obvious way to ask for the closed ones, and an application is
           * findable by the word on its chip.
           */
          filter={(row) => (row.kind === 'retailer'
            ? [
              row.retailer.name, row.retailer.email, row.retailer.city, row.retailer.countryCode,
              row.retailer.status.replace('_', ' '), row.retailer.tier, row.retailer.accountManager,
            ].filter(Boolean).join(' ')
            : [
              row.application.businessName, row.application.city, row.application.countryCode,
              'application waiting', row.application.status.replace('_', ' '),
            ].filter(Boolean).join(' '))}
          rowClassName={(row) => (row.kind === 'application' ? 'mk-row-waiting' : undefined)}
          empty={<Empty title="No retailers yet" body="Approved applications appear here." />}
        />
      </Panel>
    </>
  );
}
